import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { FileTextIcon, FlagIcon, MessageCircleIcon, UserIcon } from '../ui/icons';

/**
 * 활동 타입별 아이콘 반환
 */
const getActivityIcon = (type: string) => {
  switch (type) {
    case 'SURVEY':
      return <FileTextIcon className="w-5 h-5 text-purple-600" />;
    case 'REPORT':
      return <FlagIcon className="w-5 h-5 text-red-600" />;
    case 'INQUIRY':
      return <MessageCircleIcon className="w-5 h-5 text-blue-600" />;
    case 'USER':
      return <UserIcon className="w-5 h-5 text-green-600" />;
    default:
      return <FileTextIcon className="w-5 h-5 text-gray-600" />;
  }
};

/**
 * 상대 시간 계산 (예: "5분 전", "1시간 전")
 */
const getRelativeTime = (timestamp: string): string => {
  const now = new Date();
  const past = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}초 전`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}분 전`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}시간 전`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}일 전`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  return `${diffInWeeks}주 전`;
};

/**
 * 최근 활동 피드 컴포넌트
 */
export const RecentActivityFeed: React.FC = () => {
  const { data: activities, isLoading } = useQuery({
    queryKey: ['recentActivities'],
    queryFn: async () => {
      const response = await adminApi.getRecentActivities();
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <h3 className="text-lg font-semibold text-[#030213] mb-4">최근 활동</h3>
        <div className="flex items-center justify-center py-8">
          <p className="text-[#6B7280]">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <h3 className="text-lg font-semibold text-[#030213] mb-4">최근 활동</h3>
        <div className="flex items-center justify-center py-8">
          <p className="text-[#6B7280]">활동 내역이 없습니다</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
      <h3 className="text-lg font-semibold text-[#030213] mb-4">최근 활동</h3>
      <div className="space-y-3">
        {activities.map((activity, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {/* 아이콘 */}
            <div className="flex-shrink-0 mt-0.5">{getActivityIcon(activity.type)}</div>

            {/* 메시지 및 시간 */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#030213]">{activity.message}</p>
              <p className="text-xs text-[#6B7280] mt-1">
                {getRelativeTime(activity.timestamp)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
